import React, { useState, useCallback } from "react";
import { useDebounce } from "@uidotdev/usehooks";
import SegmentedViewHeader from "./SegmentedViewHeader";
import SegmentedWordsView, {
  SegmentedWordsViewHandle,
} from "./SegmentedWordsView";
import SegmentedPhrasesView, {
  SegmentedPhrasesViewHandle,
} from "./SegmentedPhrasesView";
import TranslateModal from "./TranslateModal";
import { ContentType } from "./WordsPhrasesTabs";
import { useDictionaryStats } from "@/hooks/useDictionaryStats";

type ViewMode = "known" | "learning";

interface DictionaryListProps {
  contentType: ContentType;
  onContentTypeChange: (type: ContentType) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onStatusUpdate: () => void;
  phraseQueryKeys: {
    knownPhrasesKey: string;
    learningPhrasesKey: string;
  };
  dictionaryStats: {
    knownWordCount: number;
    learningWordCount: number;
    knownPhraseCount: number;
    learningPhraseCount: number;
  };
}

const DEBOUNCE_TIME_MS = 300;

/**
 * DictionaryList — single continuous list (iOS Dictionary.tsx). Owns the shared
 * header (Words/Phrases tabs + Learning/Known toggle + search + add button +
 * Translate modal) and renders the active words or phrases body beneath it.
 */
const DictionaryList: React.FC<DictionaryListProps> = ({
  contentType,
  onContentTypeChange,
  viewMode,
  onViewModeChange,
  onStatusUpdate,
  phraseQueryKeys,
  dictionaryStats,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery, DEBOUNCE_TIME_MS);

  const wordsRef = React.useRef<SegmentedWordsViewHandle>(null);
  const phrasesRef = React.useRef<SegmentedPhrasesViewHandle>(null);

  const { addKnownWords, addLearningWords } = useDictionaryStats();

  // Live counts surfaced from whichever body is active (server-paginated).
  const [wordCounts, setWordCounts] = useState({
    known: dictionaryStats.knownWordCount,
    learning: dictionaryStats.learningWordCount,
  });
  const handleWordCounts = useCallback(
    (c: { known: number; learning: number }) => setWordCounts(c),
    []
  );

  const [phraseCounts, setPhraseCounts] = useState({
    known: dictionaryStats.knownPhraseCount,
    learning: dictionaryStats.learningPhraseCount,
  });
  const handlePhraseCounts = useCallback(
    (c: { known: number; learning: number }) => setPhraseCounts(c),
    []
  );

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const knownCount =
    contentType === "words" ? wordCounts.known : phraseCounts.known;
  const learningCount =
    contentType === "words" ? wordCounts.learning : phraseCounts.learning;

  const handleWordsAdded = (counts: { known: number; learning: number }) => {
    addKnownWords(counts.known);
    addLearningWords(counts.learning);
    wordsRef.current?.refreshQueries();
    onStatusUpdate();
  };

  return (
    <div className="flex flex-col w-full">
      <SegmentedViewHeader
        contentType={contentType}
        onContentTypeChange={onContentTypeChange}
        viewMode={viewMode}
        knownCount={knownCount}
        learningCount={learningCount}
        searchQuery={searchQuery}
        onViewModeChange={onViewModeChange}
        onSearchChange={handleSearchChange}
        onAddPress={() => setIsModalOpen(true)}
      />

      {contentType === "words" ? (
        <SegmentedWordsView
          ref={wordsRef}
          viewMode={viewMode}
          searchQuery={debouncedSearchQuery}
          onStatusUpdate={onStatusUpdate}
          onCountsChange={handleWordCounts}
        />
      ) : (
        <SegmentedPhrasesView
          ref={phrasesRef}
          viewMode={viewMode}
          searchQuery={debouncedSearchQuery}
          onPhraseStatusUpdate={onStatusUpdate}
          phraseQueryKeys={phraseQueryKeys}
          onCountsChange={handlePhraseCounts}
        />
      )}

      <TranslateModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onWordsAdded={handleWordsAdded}
      />
    </div>
  );
};

export default DictionaryList;
